import { Button, Flex, HStack, VStack } from "@chakra-ui/react";
import { CommunicationDetailsForm, ConfirmationForm, DrowpdownValue, PersonalDetailsForm } from "../../../types/FormTypes";
import React, { useState, useEffect } from "react";
import { communicationDetailsInitialValues, confirmationDetailsInitialValues, generatePayloadForSave, handleCommunicationDetailsValidate, handleConfirmationDetailsValidate, handlePersonalDetailsValidate, personalDetailsInitialValues } from "src/utils/registrationFormUtils";

import { AppRoutes } from "src/common/constants/constantEnums";
import { AuthService } from "src/rest/authService";
import CommunicationDetails from "./communicationDetails";
import ConfirmationDetails from "./confirmationDetails";
import { GlobalUtils } from "src/utils/globalUtils";
import HeaderComponent from "../HeaderComponent";
import LeftArrowIcon from "../../../resources/icons/LeftArrowIcon";
import PersonalDetails from "./personalDetails";
import { Regisration_Form_Labels_English } from "src/common/constants/englishLabels";
import { Regisration_Form_Labels_Hindi } from "src/common/constants/hindiLabels";
import RightArrowIcon from "../../../resources/icons/RightArrowIcon";
import { RootState } from "src/store/store";
import StepperComponent from "./StepperComponent";
import { UseAppToast } from "src/common/useAppToast";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFormInitialValuesRef } from "src/utils/formInitialValuesRef";
import { mapBackendToPersonalDetails, mapBackendToCommunicationDetails } from "src/utils/registrationFormMappers";

const RegistrationForm: React.FC = () => {
  const [personalDetailsValues, setPersonalDetailsValues] = useState<PersonalDetailsForm>(personalDetailsInitialValues);
  const [communicationDetailsValues, setCommunicationDetailsValues] = useState<CommunicationDetailsForm>(communicationDetailsInitialValues);
  const [confirmationValues, setConfirmationValues] = useState<ConfirmationForm>(confirmationDetailsInitialValues);
  const [isCurrentFormValid, setIsCurrentFormValid] = useState(false)
  const [currentStep, setCurrentStep] = useState(0);
  const { successToast } = UseAppToast();
  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const { globalLanguage } = globalInfo;
  const isHindiLabels = globalLanguage === "HI";
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const application_status = GlobalUtils.getAuthStateFromToken(token)?.applicationStatus;

  const {
    initialPersonalDetailsRef,
    initialCommunicationDetailsRef,
    initialConfirmationValuesRef,
    setInitialPersonalDetails,
    setInitialCommunicationDetails,
    setInitialConfirmationValues,
    resetAll
  } = useFormInitialValuesRef();

  const handlePersonalDetailsChanged = (fieldName: string, value: string | DrowpdownValue[]): void => {
    setPersonalDetailsValues(prevVal => ({ ...prevVal, [fieldName]: value }));
  };

  const handleCommunicationDetailsChanged = (fieldName: string, value: string | number | boolean): void => {
    setCommunicationDetailsValues(prevVal => ({ ...prevVal, [fieldName]: value }));
  };

  const handleConfirmationChanged = (fieldName: string, value: string | boolean): void => {
    setConfirmationValues(prevVal => ({ ...prevVal, [fieldName]: value }));
  };

  const handlePageChange = async (isProceed: boolean): Promise<void> => {
    const userInfo = GlobalUtils.getUserLoginInfo();
    let isValid = false;

    // Prevent proceed on final step if policy not confirmed
    if (isProceed && currentStep === 2 && !confirmationValues.isPolicyConfirmed) {
      return; // hard block
    }

    if (isProceed) {
      if (currentStep === 0) {
        isValid = handlePersonalDetailsValidate(personalDetailsValues);
      } else if (currentStep === 1) {
        isValid = handleCommunicationDetailsValidate(communicationDetailsValues);
      } else {
        isValid = handleConfirmationDetailsValidate(confirmationValues);
      }
      setIsCurrentFormValid(isValid);
    }

    //When submitting final step
    if (!isValid && isProceed && currentStep === 2) {
      // Only call API if any field has changed
      const initialPersonal = initialPersonalDetailsRef.current;
      const initialComm = initialCommunicationDetailsRef.current;
      const initialConf = initialConfirmationValuesRef.current;
      const isPersonalChanged = JSON.stringify(personalDetailsValues) !== initialPersonal;
      const isCommChanged = JSON.stringify(communicationDetailsValues) !== initialComm;
      const isConfChanged = JSON.stringify(confirmationValues) !== initialConf;
      if (!isPersonalChanged && !isCommChanged && !isConfChanged) {
        // No change, do not call API or proceed
        successToast("No changes to submit.");
        return;
      }
      const payload = generatePayloadForSave(
        currentStep,
        personalDetailsValues,
        communicationDetailsValues,
        confirmationValues
      );

      try {
        // Wait for API to finish
        await AuthService.registrationForm(
          userInfo?.emailId || "",
          userInfo?.mobileNo || "",
          currentStep,
          payload
        );

        successToast("Your application is submitted successfully!");

        const acct = GlobalUtils.getUserLoginInfo();
        const cached = {
          fullName: personalDetailsValues.fullName || (acct?.emailId || "").split("@")[0] || "",
          dob: String((personalDetailsValues as any).dateOfBirth || ""),
          contactNo: String((communicationDetailsValues as any).mobileNumber ?? acct?.mobileNo ?? ""),
          educationalQualification: String((confirmationValues as any).qualification || ""),
          occupation: String((confirmationValues as any).occupation || ""),
          businessDetails: String((confirmationValues as any).businessDetails || ""),
          applicationStatus: "Submitted",
        } as any;
        window.localStorage.setItem("cachedSubmitDetails", JSON.stringify(cached));
        window.localStorage.setItem("approved", "false");
        window.localStorage.setItem("pendingAccess", "true");

        // Short delay to ensure UI state updates, then go to Submit
        setTimeout(() => {
          navigate(AppRoutes.SUBMIT, { state: { fromApp: true } });
        }, 500);

      } catch (error) {
        console.error("Error submitting application:", error);
      }
      return;
    }

    //For intermediate steps
    else if (!isValid && isProceed && currentStep < 2) {
      const payload = generatePayloadForSave(
        currentStep,
        personalDetailsValues,
        communicationDetailsValues,
        confirmationValues
      );
      await AuthService.registrationForm(
        userInfo?.emailId || "",
        userInfo?.mobileNo || "",
        currentStep,
        payload
      );
      setCurrentStep(currentStep + 1);
    }

    //Handle back navigation
    else if (!isProceed && currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  // DRAFT RESUME LOGIC
  useEffect(() => {
    // If user is resuming a draft (applicationInfo.applicationStatus === "In_Progress")
    const applicationInfoRaw = window.localStorage.getItem("applicationInfo");
    let applicationInfo: any = null;
    try { if (applicationInfoRaw) applicationInfo = JSON.parse(applicationInfoRaw); } catch {}
    if (applicationInfo && application_status === "In_Progress") {
      const resumeStep = Number(applicationInfo.saveStatus) || 0;
      setCurrentStep(resumeStep);
      // Map backend fields to form fields for personalDetails
      if (applicationInfo.personalDetails) {
        const personalDetails = mapBackendToPersonalDetails(applicationInfo.personalDetails);
        setPersonalDetailsValues(personalDetails);
        setInitialPersonalDetails(personalDetails);
      }
      // Map backend fields to form fields for communicationDetails
      if (resumeStep >= 1 && applicationInfo.communicationDetails) {
        const communicationDetails = mapBackendToCommunicationDetails(applicationInfo);
        setCommunicationDetailsValues(communicationDetails);
        setInitialCommunicationDetails(communicationDetails);
      }
      if (resumeStep < 1) {
        setInitialCommunicationDetails(communicationDetailsInitialValues());
      }
      setInitialConfirmationValues(confirmationDetailsInitialValues());
      return;
    }
    // If coming from /register (new registration), clear all cached draft data
    if (window.location.pathname === "/register") {
      window.localStorage.removeItem("applicationInfo");
      setCurrentStep(0);
      setPersonalDetailsValues(personalDetailsInitialValues);
      setCommunicationDetailsValues(communicationDetailsInitialValues);
      setConfirmationValues(confirmationDetailsInitialValues);
      resetAll();
    }
  }, []);

  // Save as Draft handler (steps 0 and 1 only, no validation)
  const handleSaveAsDraft = async (): Promise<void> => {
    if (currentStep > 1) return;
    const userInfo = GlobalUtils.getUserLoginInfo();
    // Use the same payload flattening as submit/proceed
    const payload = generatePayloadForSave(
      currentStep,
      personalDetailsValues,
      communicationDetailsValues,
      confirmationValues
    );
    try {
      await AuthService.registrationForm(
        userInfo?.emailId || "",
        userInfo?.mobileNo || "",
        currentStep,
        payload
      );
      // Show toast, logout, clear cached form data, redirect to landing
      successToast("Draft saved successfully. You can log in later to continue where you left off.");
    } catch (error) {
      console.error("Error saving draft:", error);
    }
  };

  return (
    <VStack width="100%" px="2rem" pb="2rem">
      <HStack width="100%" mb="2rem">
        <HeaderComponent />
      </HStack>
      <VStack px={10} width="100%" justifyContent="end" mb={10}>
        <HStack justifyContent="center" width={["100%","70%","70%","70%"]}>
          <StepperComponent currentStep={currentStep} />
        </HStack>
        <VStack px={[0, 0, 0, 20]} justifyContent="center" width="100%" my={4} py={4} borderTop="1px solid #D5DBE3" borderBottom="1px solid #D5DBE3">
          {currentStep === 0 ?
            <PersonalDetails
              isCurrentFormValid={isCurrentFormValid}
              handlePersonalDetailsChanged={handlePersonalDetailsChanged}
              personalDetailsValues={personalDetailsValues}
            />
            : currentStep === 1 ?
              <CommunicationDetails
                communicationDetailsValues={communicationDetailsValues}
                handleCommunicationDetailsChanged={handleCommunicationDetailsChanged}
                isCurrentFormValid={isCurrentFormValid}
              /> :
              <ConfirmationDetails
                isCurrentFormValid={isCurrentFormValid}
                handleConfirmationChanged={handleConfirmationChanged}
                confirmationValues={confirmationValues}
              />
          }
        </VStack>
      </VStack>
      <Flex alignItems="center" gap={2} direction={["column", "column", "column", "row"]} width="100%" mb={6} justifyContent={currentStep === 0 ? "end" : "space-between"}>
        <Button
          hidden={currentStep === 0}
          ml={[0, 0, 0, 28]}
          _hover={{ backgroundColor: "grey" }}
          width={48}
          color="#4A2C2A"
          padding="16px 28px"
          border="1px solid #4A2C2A"
          backgroundColor="white"
          onClick={() => handlePageChange(false)}
          isDisabled={currentStep === 0}
        >
          <Flex alignItems={"center"} gap={2}><LeftArrowIcon />{isHindiLabels ? Regisration_Form_Labels_Hindi.GO_BACK : Regisration_Form_Labels_English.GO_BACK}</Flex>
        </Button>
        <Flex alignItems="center" justifyContent={["", "", "", "end"]} gap={2} direction={["column", "column", "column", "row"]} width="100%">
          <Button
            _hover={{ backgroundColor: "grey" }}
            mr={[0, 0, 0, 10]}
            width={48}
            padding="16px 28px"
            onClick={handleSaveAsDraft}
            color="white"
            backgroundColor="#4A2C2A"
            hidden={currentStep > 1}
          >
            {isHindiLabels ? Regisration_Form_Labels_Hindi.SAVE : Regisration_Form_Labels_English.SAVE}
          </Button>

          <Button
            _hover={{ backgroundColor: "grey", cursor: currentStep === 2 && !confirmationValues.isPolicyConfirmed ? "not-allowed" : "pointer" }}
            isDisabled={currentStep === 2 && !confirmationValues.isPolicyConfirmed}
            mr={[0, 0, 0, 28]}
            width={48}
            padding="16px 28px"
            color="white"
            backgroundColor={currentStep === 2 && !confirmationValues.isPolicyConfirmed ? "grey" : "#4A2C2A"}
            onClick={() => handlePageChange(true)}
          >
            <Flex alignItems="center" gap={2}>{currentStep === 2 ? isHindiLabels ? Regisration_Form_Labels_Hindi.SUBMIT : Regisration_Form_Labels_English.SUBMIT
              : isHindiLabels ? Regisration_Form_Labels_Hindi.PROCEED : Regisration_Form_Labels_English.PROCEED}<RightArrowIcon /></Flex>
          </Button>
        </Flex>
      </Flex>
    </VStack>
  );
};

export default RegistrationForm;